/*
 * GIF sprite logo hover controller — light/dark theme variants.
 * Desktop: play while hovered, finish cycle on mouse-leave.
 * Mobile (hover: none): play once after page load, and again after
 * theme toggle ([data-mode-toggle]).
 * See scripts/build-cb-logo-sprite.py for asset generation.
 */
(function () {
  var HOVER_TARGET = '.logo, .logo-block';
  var GIF_SELECTOR = '.cb-logo-gif';
  var ACTIVE_CLASS = 'cb-logo-gif-active';
  var READY_CLASS = 'cb-logo-gif--ready';
  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var NO_HOVER =
    window.matchMedia && window.matchMedia('(hover: none)').matches;

  var metaCache = null;
  var metaPromise = null;
  var mobileAutoplayDone = false;
  var playAfterThemeChange = false;

  function currentTheme() {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  function readFallbackMeta(container) {
    var fw = parseInt(container.getAttribute('data-frame-width'), 10) || 540;
    var fh = parseInt(container.getAttribute('data-frame-height'), 10) || 260;
    var count = parseInt(container.getAttribute('data-frame-count'), 10) || 91;
    return {
      frameCount: count,
      frameWidth: fw,
      frameHeight: fh,
      spriteWidth: fw * count,
      spriteHeight: fh,
      delaysMs: null,
      uniformDelayMs: 30,
    };
  }

  function resolveSpriteUrl(container, meta) {
    var theme = currentTheme();
    var themedUrl =
      theme === 'dark'
        ? container.getAttribute('data-sprite-dark')
        : container.getAttribute('data-sprite-light');

    if (themedUrl) return themedUrl;

    if (meta) {
      var path =
        theme === 'dark'
          ? meta.spritePathDark || meta.spritePath
          : meta.spritePathLight || meta.spritePath;
      var fallback = container.getAttribute('data-sprite') || '';
      if (path && fallback) {
        return fallback.replace(/[^/?#]+(?=$|[?#])/, path);
      }
    }

    return container.getAttribute('data-sprite') || '';
  }

  function loadMeta(container) {
    if (metaCache) return Promise.resolve(metaCache);
    if (metaPromise) return metaPromise;

    var metaUrl = container.getAttribute('data-meta-url');
    if (!metaUrl) {
      metaCache = readFallbackMeta(container);
      return Promise.resolve(metaCache);
    }

    metaPromise = fetch(metaUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('meta fetch failed');
        return response.json();
      })
      .then(function (data) {
        metaCache = data;
        return data;
      })
      .catch(function () {
        metaCache = readFallbackMeta(container);
        return metaCache;
      });

    return metaPromise;
  }

  function preloadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        resolve(true);
      };
      img.onerror = function () {
        reject(new Error('sprite load failed'));
      };
      img.src = url;
    });
  }

  function logoHeightPx(logo) {
    var styles = window.getComputedStyle(logo);
    var raw = styles.getPropertyValue('--cb-logo-h').trim();
    if (raw) return parseFloat(raw) || 36;
    raw = styles.getPropertyValue('--logo-height').trim();
    if (raw) return parseFloat(raw) || 36;
    return 36;
  }

  function SpritePlayer(logo, container) {
    this.logo = logo;
    this.container = container;
    this.surface = document.createElement('span');
    this.surface.className = 'cb-logo-gif__surface';
    this.surface.setAttribute('aria-hidden', 'true');
    this.container.appendChild(this.surface);
    this.frame = 0;
    this.playing = false;
    this.stopAfterCycle = false;
    this.timer = null;
    this.ready = false;
    this.theme = null;
    this.spriteUrl = null;
  }

  SpritePlayer.prototype.clearTimer = function () {
    if (this.timer != null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  };

  SpritePlayer.prototype.reset = function () {
    this.clearTimer();
    this.playing = false;
    this.stopAfterCycle = false;
    this.ready = false;
    this.theme = null;
    this.spriteUrl = null;
    this.frame = 0;
    this.container.classList.remove(READY_CLASS);
    this.logo.classList.remove(ACTIVE_CLASS);
    if (this._initPromise) {
      this._initPromise = null;
    }
  };

  SpritePlayer.prototype.setup = function (meta, spriteUrl) {
    var logoH = logoHeightPx(this.logo);
    var scale = logoH / meta.frameHeight;

    this.meta = meta;
    this.scale = scale;
    this.theme = currentTheme();
    this.spriteUrl = spriteUrl;
    this.container.style.width = Math.round(meta.frameWidth * scale) + 'px';
    this.container.style.height = Math.round(logoH) + 'px';

    this.surface.style.width = meta.frameWidth + 'px';
    this.surface.style.height = meta.frameHeight + 'px';
    this.surface.style.backgroundImage = 'url("' + spriteUrl + '")';
    this.surface.style.backgroundSize =
      (meta.spriteWidth || meta.frameWidth * meta.frameCount) + 'px ' + meta.frameHeight + 'px';
    this.surface.style.transform = 'scale(' + scale + ')';

    this.frame = 0;
    this.applyFrame();
    this.ready = true;
    this.container.classList.add(READY_CLASS);
  };

  SpritePlayer.prototype.getDelay = function (frameIndex) {
    if (this.meta.delaysMs && this.meta.delaysMs[frameIndex] != null) {
      return this.meta.delaysMs[frameIndex];
    }
    return this.meta.uniformDelayMs || 30;
  };

  SpritePlayer.prototype.applyFrame = function () {
    this.surface.style.backgroundPosition = -this.frame * this.meta.frameWidth + 'px 0';
  };

  /**
   * @param {boolean} [once] If true, play a single cycle then stop on frame 0.
   */
  SpritePlayer.prototype.play = function (once) {
    var self = this;
    if (REDUCED_MOTION || !this.ready) return;
    this.stopAfterCycle = !!once;
    if (this.playing) return;
    this.playing = true;

    function tick() {
      if (!self.playing) return;
      self.applyFrame();
      var delay = self.getDelay(self.frame);
      var next = (self.frame + 1) % self.meta.frameCount;

      self.timer = window.setTimeout(function () {
        if (self.stopAfterCycle && next === 0) {
          self.frame = 0;
          self.applyFrame();
          self.playing = false;
          self.logo.classList.remove(ACTIVE_CLASS);
          return;
        }
        self.frame = next;
        tick();
      }, delay);
    }

    tick();
  };

  SpritePlayer.prototype.requestStop = function () {
    if (!this.playing) return;
    this.stopAfterCycle = true;
  };

  function getPlayer(logo, container) {
    if (!container._cbGifPlayer) {
      container._cbGifPlayer = new SpritePlayer(logo, container);
    }
    return container._cbGifPlayer;
  }

  function initPlayer(logo, container, meta, force) {
    var player = getPlayer(logo, container);
    var theme = currentTheme();
    var spriteUrl = resolveSpriteUrl(container, meta);

    if (
      !force &&
      player.ready &&
      player.theme === theme &&
      player.spriteUrl === spriteUrl
    ) {
      return Promise.resolve(player);
    }

    if (player.ready && (player.theme !== theme || player.spriteUrl !== spriteUrl)) {
      player.reset();
    }

    if (player._initPromise && !force) return player._initPromise;

    player._initPromise = preloadImage(spriteUrl)
      .then(function () {
        if (!player.ready) player.setup(meta, spriteUrl);
        return player;
      })
      .catch(function () {
        player._initPromise = null;
        return player;
      });

    return player._initPromise;
  }

  function playOnceVisibleLogos(meta) {
    if (REDUCED_MOTION || !meta) return;

    var nodes = document.querySelectorAll(GIF_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      (function (container) {
        var logo = container.closest(HOVER_TARGET);
        if (!logo) return;
        // Skip logos inside the closed mobile drawer — only animate visible header/footer.
        if (logo.closest('.mobile-drawer')) return;

        initPlayer(logo, container, meta).then(function (player) {
          if (!player.ready) return;
          logo.classList.add(ACTIVE_CLASS);
          player.play(true);
        });
      })(nodes[i]);
    }
  }

  function playOnceOnMobile(meta) {
    if (!NO_HOVER || REDUCED_MOTION || mobileAutoplayDone) return;
    mobileAutoplayDone = true;
    playOnceVisibleLogos(meta);
  }

  function primeStaticFrames() {
    var nodes = document.querySelectorAll(GIF_SELECTOR);
    if (!nodes.length) return;

    loadMeta(nodes[0]).then(function (meta) {
      var pending = [];
      for (var i = 0; i < nodes.length; i++) {
        var logo = nodes[i].closest(HOVER_TARGET);
        if (!logo) continue;
        pending.push(initPlayer(logo, nodes[i], meta));
      }

      Promise.all(pending).then(function () {
        playOnceOnMobile(meta);
      });
    });
  }

  function refreshAllPlayers() {
    var nodes = document.querySelectorAll(GIF_SELECTOR);
    if (!nodes.length || !metaCache) {
      return Promise.resolve();
    }

    var pending = [];
    for (var i = 0; i < nodes.length; i++) {
      var logo = nodes[i].closest(HOVER_TARGET);
      if (!logo) continue;
      pending.push(initPlayer(logo, nodes[i], metaCache, true));
    }

    var shouldPlay = playAfterThemeChange && NO_HOVER;
    playAfterThemeChange = false;

    return Promise.all(pending).then(function () {
      if (shouldPlay) playOnceVisibleLogos(metaCache);
    });
  }

  // Capture phase: set the flag BEFORE header.js toggles data-theme,
  // so the MutationObserver sees playAfterThemeChange === true on first click.
  document.addEventListener(
    'click',
    function (e) {
      if (!NO_HOVER || REDUCED_MOTION) return;
      var toggle = e.target && e.target.closest ? e.target.closest('[data-mode-toggle]') : null;
      if (!toggle) return;
      playAfterThemeChange = true;
    },
    true
  );

  document.addEventListener('mouseover', function (e) {
    if (NO_HOVER) return;
    var logo = e.target && e.target.closest ? e.target.closest(HOVER_TARGET) : null;
    if (!logo) return;
    var container = logo.querySelector(GIF_SELECTOR);
    if (!container) return;

    loadMeta(container).then(function (meta) {
      initPlayer(logo, container, meta).then(function (player) {
        player.stopAfterCycle = false;
        logo.classList.add(ACTIVE_CLASS);
        player.play(false);
      });
    });
  });

  document.addEventListener('mouseout', function (e) {
    if (NO_HOVER) return;
    var logo = e.target && e.target.closest ? e.target.closest(HOVER_TARGET) : null;
    if (!logo) return;
    if (e.relatedTarget && logo.contains(e.relatedTarget)) return;

    var container = logo.querySelector(GIF_SELECTOR);
    if (!container || !container._cbGifPlayer) return;
    container._cbGifPlayer.requestStop();
  });

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'data-theme') {
          refreshAllPlayers();
          break;
        }
      }
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', primeStaticFrames);
  } else {
    primeStaticFrames();
  }
})();
